<template>
  <div class="flex flex-col min-h-screen">
    <Header />
    <Footer />

    <div
      v-if="showBackToTop"  
      @click="scrollToTop"
      class="scroll-to-top fixed bottom-4 left-4 z-50 cursor-pointer"
    >
      <div class="relative w-12 h-12">
        <img src="./assets/btn-transparent-mid-bg.png" alt="Back to Top" class="w-full h-full" />
        <img
          src="./assets/icon-arrow-btn.png"
          alt="Arrow"
          class="absolute inset-0 m-auto w-6 h-6"
        />
      </div>
    </div>
  
  </div>
</template>

<script setup>
import Header from "./components/Header.vue"
import Footer from "./components/Footer.vue"
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useHelpCenterStore } from '@/stores/helpCenter'
import { useBrowserMode } from '@/composables/useBrowserMode'
import { useLogger } from '@/composables/useLogger'
import { onChatOpenClose, enableLauncherLoadingFeedback } from '@/services/meya/observers'
import { BACKGROUND_LOW_RES_URL, BACKGROUND_HIGH_RES_URL } from '@/config/endpoints'

const helpCenter = useHelpCenterStore()
helpCenter.loadState()

const route = useRoute()
const { isBrowser } = useBrowserMode()
const { logClick } = useLogger()

const showBackToTop = ref(false)

let stopChatObserver = null
let launcherFeedback = null

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function updateBackToTopVisibility() {
  const canScroll = document.documentElement.scrollHeight > window.innerHeight
  const isScrolledDown = window.scrollY > 50
  showBackToTop.value = canScroll && isScrolledDown
}

let currentBgMode = null
function applyBackground() {
  const appElement = document.getElementById('app')
  if (!appElement) return

  if (!isBrowser.value) {
    if (currentBgMode === 'transparent') return
    appElement.style.background = 'transparent'
    currentBgMode = 'transparent'
    return
  }

  if (currentBgMode === 'browser') return

  appElement.style.background = `url('${BACKGROUND_LOW_RES_URL}') no-repeat center top / cover`
  appElement.style.backgroundAttachment = 'fixed'

  const img = new Image()
  img.src = BACKGROUND_HIGH_RES_URL
  img.onload = () => {
    appElement.style.background = `url('${img.src}') no-repeat center top / cover`
    appElement.style.backgroundAttachment = 'fixed'
  }

  currentBgMode = 'browser'
}

function retargetLinksForBrowserMode() {
  if (!isBrowser.value) return
  document.querySelectorAll('a').forEach(link => {
    if (link.closest('.breadcrumbs')) return
    link.setAttribute('target', '_blank')
  })
}

const syncRouteEffects = async () => {
  applyBackground()
  document.body.classList.toggle('browser-mode', isBrowser.value)
  await nextTick()
  retargetLinksForBrowserMode()
}

onMounted(() => {
  updateBackToTopVisibility()
  window.addEventListener('scroll', updateBackToTopVisibility)
  window.addEventListener('resize', updateBackToTopVisibility)
  syncRouteEffects()

  launcherFeedback = enableLauncherLoadingFeedback()
  stopChatObserver = onChatOpenClose({
    onOpen: () => {
      launcherFeedback?.reset()
      logClick('main.meyaOpened', 'main.meya')
    },
    onClose: () => logClick('main.meyaClosed', 'main.meya')
  })
})

watch(() => route.fullPath, syncRouteEffects, { flush: 'post' })

onUnmounted(() => {
  window.removeEventListener('scroll', updateBackToTopVisibility)
  window.removeEventListener('resize', updateBackToTopVisibility)
  stopChatObserver?.()
  launcherFeedback?.stop()
})
</script>